import type { TypedSupabaseClient } from '../client';
import {
  ConfirmedMutationRejectedError,
  type CreatePaymentInput,
  type IPaymentRepository,
  type Payment,
  type PaymentAllocation,
  type PaymentBalanceRow,
  type PaymentFilters,
  type PaymentThirdPartyType,
  type PaymentType,
  type UpdatePaymentInput,
} from '../../../interfaces/src';
import { buildDocumentNumber } from '@erp/domain/numbering';
import { chunkIds, fetchAllRows } from './fetch-all-rows';
import { resolveDocNumberYear } from './doc-number-year';

const PAYMENT_TYPE_PREFIX: Record<PaymentType, string> = {
  receipt: 'ENC',
  disbursement: 'DEC',
};

const PAYMENT_TYPE_SEQUENCE: Record<PaymentType, string> = {
  receipt: 'seq_payment_receipt',
  disbursement: 'seq_payment_disbursement',
};

/**
 * Mouvement de caisse correspondant à un paiement en espèces.
 *
 * ⚠ LE SIGNE. L'ancienne version choisissait entre `sale_receipt` (recette) et
 * `payment_receipt` (décaissement) — DEUX types que `movementEffect`
 * (libs/domain/caisse) compte à +1 : de l'argent qui ENTRE. Un décaissement en
 * espèces fait pourtant l'inverse, il VIDE le tiroir. Régler 500 DT à un
 * fournisseur AJOUTAIT donc 500 DT au solde théorique, et le tiroir finissait
 * la journée à 1 000 DT du compte.
 *
 * Un décaissement est donc un `withdrawal`, dont la contrainte SQL de
 * `cash_movements` (20260418020000) exige une catégorie NON NULLE.
 */
function cashMovementForPayment(
  paymentType: PaymentType,
  thirdPartyType: PaymentThirdPartyType,
): { movementType: string; category: string | null } {
  if (paymentType === 'receipt') {
    return { movementType: 'sale_receipt', category: null };
  }
  return {
    movementType: 'withdrawal',
    category: thirdPartyType === 'supplier' ? 'supplier_payment' : 'other',
  };
}

export class SupabasePaymentRepository implements IPaymentRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: PaymentFilters): Promise<Payment[]> {
    if (filters?.documentId) {
      const { data: allocs, error: allocErr } = await this.supabase
        .from('payment_allocations')
        .select('payment_id')
        .eq('document_id', filters.documentId)
        .eq('document_type', filters.documentType ?? 'delivery');
      if (allocErr) throw allocErr;
      const paymentIds = (allocs ?? []).map(
        (a: { payment_id: string }) => a.payment_id,
      );
      if (paymentIds.length === 0) return [];

      let paymentsQuery = (this.supabase as any)
        .from('payments')
        .select('*, payment_allocations(*)')
        .in('id', paymentIds);
      if (filters.collectionStoreId !== undefined) {
        paymentsQuery =
          filters.collectionStoreId === null
            ? paymentsQuery.is('collection_store_id', null)
            : paymentsQuery.eq(
                'collection_store_id',
                filters.collectionStoreId,
              );
      }
      const { data, error } = await paymentsQuery.order('payment_date', {
        ascending: false,
      });
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map((row) =>
        mapPayment(row),
      );
    }

    // Une requête FRAÎCHE par page : les builders supabase-js sont mutables,
    // réutiliser le même accumulerait les `.range()` d'une page sur l'autre.
    const baseQuery = (chunk?: string[]) => {
      let q = (this.supabase as any)
        .from('payments')
        .select('*, payment_allocations(*)');
      if (filters?.paymentType) q = q.eq('payment_type', filters.paymentType);
      if (filters?.thirdPartyType)
        q = q.eq('third_party_type', filters.thirdPartyType);
      if (filters?.thirdPartyId)
        q = q.eq('third_party_id', filters.thirdPartyId);
      if (filters?.collectionStoreId !== undefined) {
        q =
          filters.collectionStoreId === null
            ? q.is('collection_store_id', null)
            : q.eq('collection_store_id', filters.collectionStoreId);
      }
      if (filters?.dateFrom) q = q.gte('payment_date', filters.dateFrom);
      if (filters?.dateTo) q = q.lte('payment_date', filters.dateTo);
      if (filters?.recurringPaymentId) {
        q = q.eq('recurring_payment_id', filters.recurringPaymentId);
      }
      if (chunk) q = q.in('cash_session_id', chunk);
      return q;
    };

    // Un filtre par sessions de caisse peut porter sur beaucoup d'ids (une
    // année de sessions) : on découpe pour ne pas dépasser la longueur d'URL
    // acceptée par PostgREST sur un `in=(…)`.
    if (filters?.cashSessionIds) {
      if (filters.cashSessionIds.length === 0) return [];
      const out: Payment[] = [];
      for (const chunk of chunkIds(filters.cashSessionIds)) {
        const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
          baseQuery(chunk)
            .order('payment_date', { ascending: false })
            .order('id', { ascending: false })
            .range(from, to),
        );
        out.push(...rows.map((row) => mapPayment(row)));
      }
      return out;
    }

    // PostgREST plafonne silencieusement à 1000 lignes : sur une caisse active
    // la liste était déjà tronquée sans erreur, et tout agrégat calculé
    // dessus était donc faux sans que rien ne le signale.
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      baseQuery()
        // Latest payment date first (not creation order); same-day payments
        // fall back to newest created. `id` closes the order so two pages
        // can never overlap on identical timestamps.
        .order('payment_date', { ascending: false })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to),
    );
    return rows.map((row) => mapPayment(row));
  }

  /** Projection minimale (montant, type, allocations) pour les soldes tiers
   *  et widgets d'encours — sans les colonnes larges, paginée au-delà du cap
   *  PostgREST. */
  async listBalanceRows(
    thirdPartyType?: PaymentThirdPartyType,
  ): Promise<PaymentBalanceRow[]> {
    type Row = {
      id: string;
      third_party_id: string | null;
      amount: number | null;
      payment_type: PaymentType;
      payment_allocations: Array<{
        document_id: string;
        allocated_amount: number | null;
      }> | null;
    };
    const rows = await fetchAllRows<Row>((from, to) => {
      let query = (this.supabase as any)
        .from('payments')
        .select(
          'id, third_party_id, amount, payment_type, payment_allocations(document_id, allocated_amount)',
        );
      if (thirdPartyType) query = query.eq('third_party_type', thirdPartyType);
      return query.order('id', { ascending: true }).range(from, to);
    });
    return rows.map((r) => ({
      id: r.id,
      thirdPartyId: r.third_party_id ?? '',
      amount: Number(r.amount) || 0,
      paymentType: r.payment_type,
      allocations: (r.payment_allocations ?? []).map((a) => ({
        documentId: a.document_id,
        allocatedAmount: Number(a.allocated_amount) || 0,
      })),
    }));
  }

  async getById(id: string): Promise<Payment | null> {
    const { data, error } = await this.supabase
      .from('payments')
      .select('*, payment_allocations(*)')
      .eq('id', id)
      .single();
    if (error || !data) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapPayment(data);
  }

  async resolveAtomicRequest(
    clientRequestId: string,
    requestKind: 'sales_invoice_collection' | 'payment',
  ): Promise<string | null> {
    const { data, error } = await this.supabase.rpc(
      'resolve_mobile_atomic_request',
      {
        p_client_request_id: clientRequestId,
        p_request_kind: requestKind,
        p_cancel_if_missing: false,
      },
    );
    if (error) throw error;
    return data ?? null;
  }

  async resolveOrCancelAtomicRequest(
    clientRequestId: string,
    requestKind: 'sales_invoice_collection' | 'payment',
  ): Promise<string | null> {
    const { data, error } = await this.supabase.rpc(
      'resolve_mobile_atomic_request',
      {
        p_client_request_id: clientRequestId,
        p_request_kind: requestKind,
        p_cancel_if_missing: true,
      },
    );
    if (error) throw error;
    return data ?? null;
  }

  async resolveCashSessionId(
    input: Pick<
      CreatePaymentInput,
      'paymentMethod' | 'bankAccountId' | 'collectionStoreId'
    >,
  ): Promise<string | null> {
    if (input.paymentMethod !== 'cash') return null;
    return this.resolveOpenCashSession(
      input.bankAccountId,
      input.collectionStoreId,
    );
  }

  async create(input: CreatePaymentInput): Promise<Payment> {
    const requestKind = input.atomicRequestKind ?? 'payment';
    const isInvoiceCollection = requestKind === 'sales_invoice_collection';
    if (
      isInvoiceCollection &&
      (!input.clientRequestId || !this.isAtomicInvoiceCollectionInput(input))
    ) {
      throw new Error('invalid_atomic_invoice_collection_input');
    }
    const collectionStoreId =
      input.collectionStoreId !== undefined
        ? input.collectionStoreId
        : await this.resolveDefaultStoreId();
    let cashSessionId: string | null = input.cashSessionId ?? null;
    if (input.paymentMethod === 'cash' && input.cashSessionId === undefined) {
      // Pas bloquant : si aucune caisse ouverte, on enregistre le paiement
      // sans lien de session (pas de cash_movement créé). La caisse n'est
      // obligatoire que quand on l'utilise réellement (ouverture/clôture).
      cashSessionId = await this.resolveCashSessionId({
        paymentMethod: input.paymentMethod,
        bankAccountId: input.bankAccountId,
        collectionStoreId,
      });
    }

    if (isInvoiceCollection) {
      if (!collectionStoreId) {
        throw new Error('atomic_invoice_collection_store_required');
      }
      return this.createSalesInvoiceCollectionAtomic(
        input,
        collectionStoreId,
        cashSessionId,
      );
    }

    const cashPlan = cashSessionId
      ? cashMovementForPayment(input.paymentType, input.thirdPartyType)
      : null;
    const allocations = [...(input.allocations ?? [])]
      .sort((left, right) => left.allocatedAmount - right.allocatedAmount)
      .map((allocation) => ({
        document_type: allocation.documentType,
        document_id: allocation.documentId,
        allocated_amount: allocation.allocatedAmount,
      }));
    const paymentPayload = {
      payment_type: input.paymentType,
      payment_method: input.paymentMethod,
      third_party_type: input.thirdPartyType,
      third_party_id: input.thirdPartyId,
      collection_store_id: collectionStoreId,
      bank_account_id: input.bankAccountId ?? null,
      amount: input.amount,
      payment_date: input.paymentDate,
      reference: input.reference ?? null,
      notes: input.notes ?? null,
      recurring_payment_id: input.recurringPaymentId ?? null,
      recurring_occurrence_id: input.recurringOccurrenceId ?? null,
      ...(input.recurringClaimToken
        ? { recurring_claim_token: input.recurringClaimToken }
        : {}),
    };
    const draftPayload = input.linkedDraft
      ? {
          type: input.linkedDraft.type,
          direction: input.linkedDraft.direction,
          number: input.linkedDraft.number,
          bank_name: input.linkedDraft.bankName ?? '',
          bank_account_id: input.linkedDraft.bankAccountId ?? null,
          payer_bank_id: input.linkedDraft.payerBankId ?? null,
          payer_rib: input.linkedDraft.payerRib ?? null,
          beneficiary_name: input.linkedDraft.beneficiaryName ?? null,
          place_of_payment: input.linkedDraft.placeOfPayment ?? null,
          place_of_issue: input.linkedDraft.placeOfIssue ?? null,
          protestable: input.linkedDraft.protestable ?? null,
          amount: input.linkedDraft.amount,
          issue_date: input.linkedDraft.issueDate,
          due_date: input.linkedDraft.dueDate,
          acceptance_date: input.linkedDraft.acceptanceDate ?? null,
          third_party_type: input.linkedDraft.thirdPartyType,
          third_party_id: input.linkedDraft.thirdPartyId,
          current_state: input.linkedDraft.currentState ?? null,
          is_endorsed: input.linkedDraft.isEndorsed ?? false,
          endorsed_to: input.linkedDraft.endorsedTo ?? null,
          notes: input.linkedDraft.notes ?? null,
        }
      : null;
    const requestId = input.clientRequestId ?? globalThis.crypto.randomUUID();
    const rpcArgs = {
      p_client_request_id: requestId,
      p_payment: paymentPayload,
      p_allocations: allocations,
      p_cash_session_id: cashSessionId,
      p_cash_movement_type: cashPlan?.movementType ?? null,
      p_cash_movement_category: cashPlan?.category ?? null,
      p_draft: draftPayload,
    };
    let { data: paymentId, error } = await this.supabase.rpc(
      'create_payment_atomic',
      rpcArgs,
    );
    if (
      (error && isAmbiguousDatabaseFailure(error)) ||
      (!error && !paymentId)
    ) {
      // Same durable key: an ambiguous timeout/no-body can only replay the
      // committed row or repeat the original rejection, never duplicate it.
      ({ data: paymentId, error } = await this.supabase.rpc(
        'create_payment_atomic',
        rpcArgs,
      ));
    }
    if (error || !paymentId) {
      if (error?.code === '23505') {
        // A changed payload must remain a canonical idempotency conflict. A
        // payload-free lookup would otherwise misreport the previous payment
        // as the result of the edited command.
        throw error;
      }
      if (error && isConfirmedDatabaseRejection(error)) {
        // A deterministic SQL rejection proves this invocation did not
        // commit. Resolving by key here could return an older resource whose
        // payload differs (for example after an edited retry).
        throw new ConfirmedMutationRejectedError(error);
      }
      const resolvedId = await this.resolveOrCancelAtomicRequest(
        requestId,
        'payment',
      );
      if (resolvedId) {
        const resolved = await this.getById(resolvedId);
        if (!resolved) throw new Error('atomic_payment_unavailable');
        return resolved;
      }
      if (!error) {
        throw new ConfirmedMutationRejectedError(
          new Error('atomic_payment_returned_no_id'),
        );
      }
      throw error;
    }
    const payment = await this.getById(paymentId);
    if (!payment) throw new Error('atomic_payment_unavailable');
    return payment;
  }

  private isAtomicInvoiceCollectionInput(input: CreatePaymentInput): boolean {
    const allocation = input.allocations?.[0];
    const amountMillimes = Math.round(Number(input.amount) * 1000);
    const allocationMillimes = Math.round(
      Number(allocation?.allocatedAmount) * 1000,
    );
    return !(
      input.paymentType !== 'receipt' ||
      input.thirdPartyType !== 'client' ||
      input.paymentMethod === 'draft' ||
      input.allocations?.length !== 1 ||
      allocation?.documentType !== 'invoice' ||
      !allocation.documentId ||
      !Number.isFinite(amountMillimes) ||
      amountMillimes <= 0 ||
      allocationMillimes !== amountMillimes
    );
  }

  /**
   * Direct invoice collection is a single persistence command. Amount/change
   * planning remains in @erp/domain/payments; the RPC only serializes the
   * mutable invoice balance and writes payment, allocation and cash movement
   * in one transaction.
   */
  private async createSalesInvoiceCollectionAtomic(
    input: CreatePaymentInput,
    collectionStoreId: string | null,
    cashSessionId: string | null,
  ): Promise<Payment> {
    const allocation = input.allocations?.[0];
    if (!input.clientRequestId || !allocation || !collectionStoreId) {
      throw new Error('atomic_invoice_collection_store_required');
    }
    const { data, error } = await this.supabase.rpc(
      'collect_sales_invoice_atomic',
      {
        p_client_request_id: input.clientRequestId,
        p_invoice_id: allocation.documentId,
        p_client_id: input.thirdPartyId,
        p_collection_store_id: collectionStoreId,
        p_payment_method: input.paymentMethod,
        p_bank_account_id: input.bankAccountId ?? null,
        p_amount: input.amount,
        p_payment_date: input.paymentDate,
        p_reference: input.reference ?? null,
        p_notes: input.notes ?? null,
        p_cash_session_id: cashSessionId,
      },
    );
    if (error || !data) {
      if (error?.code === '23505') {
        // The canonical writer has proved that this key belongs to a different
        // fingerprint. A payload-free resolver must never turn that conflict
        // into a successful response for the old payment.
        throw error;
      }
      if (error && isConfirmedDatabaseRejection(error)) {
        throw new ConfirmedMutationRejectedError(error);
      }
      // A network error may be observed after commit. Re-enter the same
      // request ledger before surfacing it, so the UI never invites a second
      // logical payment for an already completed collection.
      const resolvedId = await this.resolveOrCancelAtomicRequest(
        input.clientRequestId,
        'sales_invoice_collection',
      );
      if (resolvedId) {
        const resolved = await this.getById(resolvedId);
        if (!resolved) throw new Error('atomic_invoice_collection_unavailable');
        return resolved;
      }
      if (!error) {
        throw new ConfirmedMutationRejectedError(
          new Error('atomic_invoice_collection_returned_no_id'),
        );
      }
      throw error;
    }
    const payment = await this.getById(data as string);
    if (!payment) throw new Error('atomic_invoice_collection_unavailable');
    return payment;
  }

  async update(id: string, input: UpdatePaymentInput): Promise<Payment> {
    const current = await this.getById(id);
    if (!current) throw new Error('Payment not found');

    const finalMethod = input.paymentMethod ?? current.paymentMethod;
    const finalBankAccountId =
      input.bankAccountId !== undefined
        ? input.bankAccountId
        : current.bankAccountId;
    const finalCollectionStoreId =
      input.collectionStoreId !== undefined
        ? input.collectionStoreId
        : current.collectionStoreId;
    const finalAmount =
      input.amount !== undefined ? input.amount : current.amount;
    const finalPaymentDate = input.paymentDate ?? current.paymentDate;
    const finalReference =
      input.reference !== undefined ? input.reference : current.reference;
    const finalNotes = input.notes !== undefined ? input.notes : current.notes;
    const amountChanged =
      input.amount !== undefined &&
      Math.abs(input.amount - current.amount) > 0.0005;
    // Une traite liée déjà en circulation (remise, encaissée…) fige le montant
    // et le mode : la corriger doit passer par le module Chèques & Traites.
    const linkedDrafts = await this.listLinkedCheckDrafts(id);
    const progressedDraft = linkedDrafts.find(
      (d) => !isInitialCheckState(d.current_state),
    );
    if (
      progressedDraft &&
      (amountChanged ||
        (input.paymentMethod !== undefined &&
          input.paymentMethod !== current.paymentMethod))
    ) {
      throw new Error(
        `La traite liée ${progressedDraft.number ?? ''} n'est plus en portefeuille — modifiez-la d'abord dans Chèques & Traites.`,
      );
    }
    const methodChanged = finalMethod !== current.paymentMethod;
    const drawerChanged = finalBankAccountId !== current.bankAccountId;
    const storeChanged = finalCollectionStoreId !== current.collectionStoreId;
    let sessionId: string | null = null;
    if (finalMethod === 'cash') {
      sessionId =
        !methodChanged && !drawerChanged && !storeChanged
          ? current.cashSessionId
          : await this.resolveOpenCashSession(
              finalBankAccountId ?? undefined,
              finalCollectionStoreId,
            );
    }
    const plan = sessionId
      ? cashMovementForPayment(current.paymentType, current.thirdPartyType)
      : null;
    const allocationPayload =
      input.allocations === undefined
        ? null
        : [...input.allocations]
            .sort((left, right) => left.allocatedAmount - right.allocatedAmount)
            .map((allocation) => ({
              document_type: allocation.documentType,
              document_id: allocation.documentId,
              allocated_amount: allocation.allocatedAmount,
            }));
    const requestId = input.clientRequestId ?? globalThis.crypto.randomUUID();
    const rpcArgs = {
      p_client_request_id: requestId,
      p_payment_id: id,
      p_payment: {
        payment_method: finalMethod,
        collection_store_id: finalCollectionStoreId,
        bank_account_id: finalBankAccountId,
        amount: finalAmount,
        payment_date: finalPaymentDate,
        reference: finalReference,
        notes: finalNotes,
        cash_session_id: sessionId,
        cash_movement_type: plan?.movementType ?? null,
        cash_movement_category: plan?.category ?? null,
      },
      p_allocations: allocationPayload,
    };
    let { data: paymentId, error } = await this.supabase.rpc(
      'update_payment_atomic',
      rpcArgs,
    );
    if (
      (error && isAmbiguousDatabaseFailure(error)) ||
      (!error && !paymentId)
    ) {
      ({ data: paymentId, error } = await this.supabase.rpc(
        'update_payment_atomic',
        rpcArgs,
      ));
    }
    if (error) throw error;
    if (!paymentId) throw new Error('atomic_payment_update_returned_no_id');
    const updated = await this.getById(paymentId);
    if (!updated) throw new Error('Payment not found after update');
    return updated;
  }

  private async listLinkedCheckDrafts(
    paymentId: string,
  ): Promise<LinkedCheckDraftRow[]> {
    const { data, error } = await this.supabase
      .from('checks_drafts')
      .select('id, number, amount, current_state')
      .eq('payment_id', paymentId);
    if (error) throw error;
    return (data ?? []) as LinkedCheckDraftRow[];
  }

  /**
   * Retourne l'id de la caisse ouverte à laquelle rattacher un encaissement en
   * espèces, ou null si le tiroir ne peut pas être déterminé.
   *
   * C'est CE point qui fait exister la caisse sans écran POS : un encaissement
   * saisi depuis une facture, la page Encaissements ou le mobile doit tomber
   * dans le tiroir comme s'il avait été tapé sur le POS.
   *
   * L'ordre de résolution est explicite → configuré → déduit :
   *
   *   1. le tiroir passé explicitement par l'appelant ;
   *   2. la CAISSE PAR DÉFAUT du magasin de collecte ;
   *   3. l'unique session ouverte dans ce magasin, s'il n'y en a qu'une.
   *
   * Le réglage `caisse_settings.auto_attach_cash_payments` gouverne UNIQUEMENT
   * le repli implicite (étapes 2 et 3) : c'est exactement ce qu'il promet dans
   * l'UI — « rattacher automatiquement ». Un tiroir choisi explicitement par
   * l'utilisateur reste rattaché, sinon le choix serait ignoré sans rien dire.
   * Désactivé, un paiement espèces sans tiroir explicite ne tombe dans AUCUNE
   * session et ne crée AUCUN mouvement de caisse.
   *
   * L'étape 2 est indispensable depuis l'ouverture automatique : un commerce à
   * deux caisses a désormais en permanence deux sessions ouvertes, si bien que
   * l'ancienne heuristique « exactement une session ouverte » (étape 3, seule à
   * l'époque) renvoyait null en permanence — chaque encaissement hors POS
   * repartait alors sans session ET sans mouvement de caisse, silencieusement,
   * jusqu'à ce que le tiroir ne tombe plus juste en fin de journée.
   *
   * Rester non bloquant est volontaire : un paiement ne doit jamais échouer
   * parce que la caisse est mal configurée. Il est simplement conservé sans
   * lien de session.
   */
  private async resolveOpenCashSession(
    bankAccountId?: string,
    collectionStoreId?: string | null,
  ): Promise<string | null> {
    if (bankAccountId) {
      if (!collectionStoreId) throw new Error('cash_payment_store_required');
      const { data: account, error: accountError } = await (
        this.supabase as any
      )
        .from('bank_accounts')
        .select('id, account_type, store_id')
        .eq('id', bankAccountId)
        .maybeSingle();
      if (accountError) throw accountError;
      if (!account) throw new Error('cash_register_not_accessible');
      if (account.account_type !== 'cash')
        throw new Error('cash_register_required');
      if (account.store_id !== collectionStoreId) {
        throw new Error('cash_register_store_mismatch');
      }
      const { data, error } = await this.supabase
        .from('cash_sessions')
        .select('id')
        .eq('bank_account_id', bankAccountId)
        .eq('is_closed', false)
        .maybeSingle();
      if (error) throw error;
      return data ? (data as { id: string }).id : null;
    }

    if (!(await this.autoAttachEnabled())) return null;

    const storeId = collectionStoreId ?? (await this.resolveDefaultStoreId());

    let defaultRegisterQuery = (this.supabase as any)
      .from('bank_accounts')
      .select('id')
      .eq('account_type', 'cash')
      .eq('is_default_cash_register', true);
    if (storeId)
      defaultRegisterQuery = defaultRegisterQuery.eq('store_id', storeId);
    const { data: defaultRegister, error: registerError } =
      await defaultRegisterQuery.maybeSingle();
    if (registerError) throw registerError;

    if (defaultRegister) {
      const { data, error } = await this.supabase
        .from('cash_sessions')
        .select('id')
        .eq('bank_account_id', (defaultRegister as { id: string }).id)
        .eq('is_closed', false)
        .maybeSingle();
      if (error) throw error;
      if (data) return (data as { id: string }).id;
    }

    let openSessionsQuery = (this.supabase as any)
      .from('cash_sessions')
      .select('id, bank_accounts!inner(store_id)')
      .eq('is_closed', false);
    if (storeId)
      openSessionsQuery = openSessionsQuery.eq(
        'bank_accounts.store_id',
        storeId,
      );
    const { data, error } = await openSessionsQuery;
    if (error) throw error;
    const sessions = data ?? [];
    if (sessions.length !== 1) return null;
    return (sessions[0] as { id: string }).id;
  }

  private async resolveDefaultStoreId(): Promise<string | null> {
    try {
      const { data, error } = await (this.supabase as any)
        .from('authorized_stores')
        .select('id')
        .eq('is_default', true)
        .maybeSingle();
      if (error) return null;
      return (data?.id as string | undefined) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * `caisse_settings.auto_attach_cash_payments`, lu une seule fois par
   * résolution et seulement sur le chemin implicite.
   *
   * Ligne absente ou illisible → `true`, aligné sur le DEFAULT SQL et sur
   * DEFAULT_CAISSE_SETTINGS. Un réglage inaccessible ne doit jamais faire
   * échouer un paiement (même principe que le reste de cette méthode) : on
   * retombe sur le comportement historique plutôt que de refuser l'écriture.
   */
  private async autoAttachEnabled(): Promise<boolean> {
    try {
      const { data, error } = await this.supabase
        .from('caisse_settings')
        .select('auto_attach_cash_payments')
        .maybeSingle();
      if (error || !data) return true;
      return (
        (data as { auto_attach_cash_payments: boolean | null })
          .auto_attach_cash_payments !== false
      );
    } catch {
      return true;
    }
  }

  async delete(id: string): Promise<void> {
    // Traite/chèque auto-créés avec le paiement : suppression bloquée dès
    // qu'une pièce est en circulation (remise en banque, encaissée, débitée…),
    // sinon la pièce encore en portefeuille part avec le paiement.
    const linkedDrafts = await this.listLinkedCheckDrafts(id);
    const progressed = linkedDrafts.find(
      (d) => !isInitialCheckState(d.current_state),
    );
    if (progressed) {
      throw new Error(
        `La traite liée ${progressed.number ?? ''} n'est plus en portefeuille — impossible de supprimer ce paiement.`,
      );
    }
    const { error } = await this.supabase.rpc('delete_payment_atomic', {
      p_payment_id: id,
    });
    if (error) throw error;
  }

  async getNextNumber(type: PaymentType): Promise<string> {
    const prefix = PAYMENT_TYPE_PREFIX[type];
    const seqName = PAYMENT_TYPE_SEQUENCE[type];
    const year = await resolveDocNumberYear(this.supabase);

    if (seqName) {
      const { data, error } = await this.supabase.rpc(
        'get_next_sequence_value',
        {
          seq_name: seqName,
        },
      );
      if (error) throw error;
      if (data == null) throw new Error('atomic_payment_number_required');
      return buildDocumentNumber(prefix, year, Number(data));
    }
    throw new Error('atomic_payment_number_required');
  }
}

interface LinkedCheckDraftRow {
  id: string;
  number: string | null;
  amount: number;
  current_state: string | null;
}

/** États « en portefeuille » : la pièce n'a pas encore circulé en banque. */
function isInitialCheckState(state: string | null): boolean {
  return state === null || state === 'in_wallet' || state === 'issued';
}

function isConfirmedDatabaseRejection(error: {
  code?: string | null;
}): boolean {
  return (
    typeof error.code === 'string' &&
    /^[0-9A-Z]{5}$/.test(error.code) &&
    error.code !== '23505'
  );
}

function isAmbiguousDatabaseFailure(error: { code?: string | null }): boolean {
  return typeof error.code !== 'string' || !/^[0-9A-Z]{5}$/.test(error.code);
}

function mapPayment(row: Record<string, unknown>): Payment {
  const allocations =
    (row['payment_allocations'] as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    paymentType: row['payment_type'] as Payment['paymentType'],
    paymentMethod: row['payment_method'] as Payment['paymentMethod'],
    thirdPartyType: row['third_party_type'] as Payment['thirdPartyType'],
    thirdPartyId: row['third_party_id'] as string,
    collectionStoreId: (row['collection_store_id'] as string | null) ?? null,
    bankAccountId: (row['bank_account_id'] as string) ?? null,
    cashSessionId: (row['cash_session_id'] as string) ?? null,
    amount: row['amount'] as number,
    paymentDate: row['payment_date'] as string,
    reference: (row['reference'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    recurringPaymentId: (row['recurring_payment_id'] as string) ?? null,
    createdAt: row['created_at'] as string,
    allocations: allocations.map(mapPaymentAllocation),
  };
}

function mapPaymentAllocation(row: Record<string, unknown>): PaymentAllocation {
  return {
    id: row['id'] as string,
    paymentId: row['payment_id'] as string,
    documentType: row['document_type'] as string,
    documentId: row['document_id'] as string,
    allocatedAmount: row['allocated_amount'] as number,
  };
}
